import { useEffect, useState } from "react";

/**
 * Custom hook that manages animation state with cookie tracking
 * @param duration - Duration in milliseconds before animation stops (default: 4000ms)
 * @param cookieName - Name of the cookie to track animation state (default: 'filter_animation_shown')
 * @param cookieExpireDays - Number of days before cookie expires (default: 30)
 * @returns shouldAnimate - boolean state that considers both timer and cookie state
 */
const useAnimationWithCookie = (
  duration = 4000,
  cookieName = "filter_animation_shown",
  cookieExpireDays = 30,
): boolean => {
  const [shouldAnimate, setShouldAnimate] = useState(true);

  // Helper function to get cookie value
  const getCookie = (name: string): string | null => {
    if (typeof document === "undefined") return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null;
    }
    return null;
  };

  // Helper function to set cookie
  const setCookie = (name: string, value: string, days: number): void => {
    if (typeof document === "undefined") return;

    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  useEffect(() => {
    // Check if animation has been shown before
    const hasAnimationBeenShown = getCookie(cookieName) === "true";

    if (hasAnimationBeenShown) {
      setShouldAnimate(false);
      return; // Don't set timer if animation was already shown
    }

    // Set timer to stop animation and set cookie
    const timer = setTimeout(() => {
      setShouldAnimate(false);
      setCookie(cookieName, "true", cookieExpireDays);
    }, duration);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [duration, cookieName, cookieExpireDays]);

  return shouldAnimate;
};

export default useAnimationWithCookie;
